import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Close } from '@mui/icons-material';
import {
  Application,
  ApplicationAction,
  ApplicationActionButtonType,
} from '@features/applications/types';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import SaveIcon from '@mui/icons-material/Save';
import { PersonIcon } from '../../../../vectors/index';
import { applicationActions } from '../BulkActions/constants';
import SingleMessageModal from '../SingleMessageModal/SingleMessageModal';
import {
  getCTAButtonColor,
  getEndpointForApplicationAction,
  getSendWithMessageButtonText,
  getSendWithoutMessageButtonText,
  getTitle,
  getSuccessMessage as getSuccessMessageFromActionTitle,
} from '../BulkActions/utils';
import { authContext } from '@staffedupusa/shared-components';
import { useSingleApplicationAction } from '@features/applications/hooks/useSingleApplicationAction';
import { toast } from 'react-toastify';
import { useEnterpriseBulkAction } from '@features/applications/hooks/useEnterpriseBulkAction';
import { useUpdateInterviewAction } from '@features/applications/hooks/useUpdateInterviewAction';
import { useBulkAction } from '@features/applications/hooks/useBulkAction';

type MarkInterviewModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  type: '' | 'completed' | 'missed';
  application?: Application;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication?: Dispatch<SetStateAction<Application | null>>;
  setIsMessagingModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedApplicationsIds?: number[];
};

const MarkInterviewModal: FC<MarkInterviewModalProps> = ({
  isOpen,
  handleClose,
  type,
  application,
  setApplication,
  setApplicationData,
  setIsMessagingModalOpen,
  selectedApplicationsIds,
}) => {
  const [interviewActionType, setInterviewActionType] = useState<
    'completed' | 'missed' | ''
  >('');
  const { authData } = useContext(authContext);
  const isCompany = authData.user?.company;
  const [errorText, setErrorText] = useState('');
  const [selectedAction, setSelectedAction] =
    useState<ApplicationAction | null>(null);

  const { enterpriseCreateBulkAction, isEnterpriseCreateBulkActionLoading } =
    useEnterpriseBulkAction({
      setErrorText,
    });

  const { updateInterviewAction, isUpdateInterviewActionLoading } =
    useUpdateInterviewAction({
      setErrorText,
    });

  const { createApplicationAction, isCreateApplicationActionLoading } =
    useSingleApplicationAction({ setErrorText });

  const getSuccessMessage = (isMessageRequired: Boolean) => {
    let successMessage = 'Messaged applications';

    const numberOfMessages = selectedApplicationsIds?.length || 1;
    const action = selectedAction ? selectedAction.actionType : '';

    const getPluralizedWord = (
      count: number,
      singular: string,
      plural: string,
    ) => {
      return count === 1 ? singular : plural;
    };

    successMessage =
      action === 'invite to interview block'
        ? `Sent ${numberOfMessages} interview ${getPluralizedWord(numberOfMessages, 'invite', 'invites')}`
        : action === 'hire' && isMessageRequired
          ? `Sent ${numberOfMessages} job ${getPluralizedWord(numberOfMessages, 'offer', 'offers')}`
          : action === 'hire' && !isMessageRequired
            ? `Moved ${numberOfMessages} to hired`
            : action === 'save'
              ? `Saved ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
              : action === 'decline'
                ? `Declined ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
                : action === 'archive'
                  ? `Archived ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
                  : action === 'Scheduled Interview'
                    ? `Sent ${numberOfMessages} interview ${getPluralizedWord(numberOfMessages, 'invite', 'invites')}`
                    : `Messaged ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`;

    return numberOfMessages ? successMessage : successMessage;
  };

  const {
    createBulkAction,
    isCreateBulkActionLoading,
  } = useBulkAction({
    setErrorText,
  });
  const handleActionClick = (
    e: { stopPropagation: () => void; preventDefault: () => void },
    actionType: ApplicationActionButtonType,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    switch (actionType) {
      case 'save':
      case 'hire':
      case 'decline':
      case 'archive':
        setSelectedAction(
          applicationActions.find((item) => item.actionType === actionType)!,
        );
        handleClose();
        return;
      case 'messenger':
        if (application) {
          updateInterviewAction(
            {
              interview_action: interviewActionType,
              application_id: application?.id,
            },
            {
              onSuccess: (data) => {
                if (data?.status == 200) {
                  const updatedSelectedApplication = data?.object.find(
                    (application: { id: any }) =>
                      application.id === application?.id,
                  );
                  setApplication && setApplication(updatedSelectedApplication);
                  setApplicationData &&
                    setApplicationData((prev) => {
                      if (!prev) return null;
                      return prev?.map((application) => {
                        if (application.id === updatedSelectedApplication.id) {
                          return updatedSelectedApplication;
                        }
                        return application;
                      });
                    });
                  setSelectedAction(null);
                }
              },
            },
          );
          setIsMessagingModalOpen && setIsMessagingModalOpen(true);
          handleClose();
          return;
        }
      default:
    }
  };
  const handleSend = (isMessageRequired: boolean, message: string) => {
    switch (selectedAction?.actionType) {
      case 'save':
      case 'hire':
      case 'decline':
        if (application) {
          return createApplicationAction(
            {
              endpoint: getEndpointForApplicationAction(selectedAction),
              payload: {
                application_id: application.id,
                ...(isMessageRequired && { message }),
                ...(selectedAction?.actionType !== 'hire' && {
                  interview_action: interviewActionType,
                }),
              },
            },
            {
              onSuccess: (data) => {
                if (data?.status == 200) {
                  toast.success(
                    getSuccessMessageFromActionTitle(isMessageRequired, selectedAction),
                  );
                  setApplication && setApplication(data?.object);
                  setApplicationData &&
                    setApplicationData((prev) => {
                      if (!prev) return null;
                      return prev?.map((application) => {
                        if (application.id === data?.object.id) {
                          return data?.object;
                        }
                        return application;
                      });
                    });
                  handleClose();
                  setSelectedAction(null);
                }
              },
            },
          );
        } else if (selectedApplicationsIds) {
          createBulkAction(
            {
              action: selectedAction?.actionType as string,
              ids: selectedApplicationsIds?.join(','),
              ...(isMessageRequired && { message }),
              ...(selectedAction?.actionType !== 'hire' && {
                interview_action: interviewActionType,
              }),
            },
            {
              onSuccess: (data) => {
                if (data?.status == 200) {
                  const updatedSelectedApplication = data?.object.find(
                    (application: { id: any }) =>
                      application.id === application?.id,
                  );
                  setApplication && setApplication(updatedSelectedApplication);
                  setApplicationData &&
                    setApplicationData((prev) => {
                      if (!prev) return null;
                      return prev?.map((application) => {
                        if (application.id === updatedSelectedApplication.id) {
                          return updatedSelectedApplication;
                        }
                        return application;
                      });
                    });
                  toast.success(
                    getSuccessMessage(isMessageRequired),
                  );
                  handleClose();
                  setSelectedAction(null);
                }
              },
            },
          );
        }
      default:
    }
  };
  const handleSendEnterprise = async (
    isMessageRequired: Boolean,
    message: string,
  ) => {
    const bulkActionPayload = {
      action: selectedAction?.actionType.toString(),
      ids: application
        ? application?.id.toString()
        : selectedApplicationsIds?.join(',') || '',
      ...(isMessageRequired && { message }),

      ...(selectedAction?.actionType === 'Invite to Open Interview' && {
        slot_interview: false,
      }),
      ...(selectedAction?.actionType === 'invite to interview block' && {
        slot_interview: true,
      }),
      ...(selectedAction?.actionType !== 'hire' && {
        interview_action: interviewActionType,
      }),
    };
    enterpriseCreateBulkAction(bulkActionPayload, {
      onSuccess: (data) => {
        if (data?.status == 200) {
          const updatedSelectedApplication = data?.object.find(
            (application: { id: any }) => application.id === application?.id,
          );
          setApplication && setApplication(updatedSelectedApplication);
          setApplicationData &&
            setApplicationData((prev) => {
              if (!prev) return null;
              return prev?.map((application) => {
                if (application.id === updatedSelectedApplication.id) {
                  return updatedSelectedApplication;
                }
                return application;
              });
            });
          toast.success(selectedApplicationsIds ? getSuccessMessage(isMessageRequired) : getSuccessMessageFromActionTitle(isMessageRequired, selectedAction!));
          handleClose();
          setSelectedAction(null);
        }
      },
    });
  };

  useEffect(() => {
    if (type) {
      setInterviewActionType(type);
    }
  }, [type]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          handleClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ position: 'relative', pb: 0 }}>
          <IconButton
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleClose();
            }}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Close />
          </IconButton>
          <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
            <PersonIcon />
            <Typography variant="h6" mt={2} textAlign="center">
              What would you like to do with
            </Typography>
            <Typography variant="h5" fontWeight="bold" textAlign="center">
              {application?.applicant?.name ??
                `${selectedApplicationsIds?.length} applications`}
              ?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2} alignItems="center">
            {type === 'completed' && (
              <Button
                fullWidth
                variant="contained"
                color="lightPurple"
                startIcon={<CheckIcon />}
                onClick={(e) => handleActionClick(e, 'hire')}
              >
                Send Job Offer
              </Button>
            )}
            <Button
              fullWidth
              color="success"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={(e) => handleActionClick(e, 'save')}
            >
              Save Application
            </Button>
            <Button
              fullWidth
              color="error"
              variant="contained"
              startIcon={<CloseIcon />}
              onClick={(e) => handleActionClick(e, 'decline')}
            >
              Decline Candidate
            </Button>
            {application && (
              <Button
                fullWidth
                color="primary"
                variant="outlined"
                startIcon={<MessageIcon />}
                onClick={(e) => handleActionClick(e, 'messenger')}
              >
                Send Message
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
      {Boolean(selectedAction) && (
        <SingleMessageModal
          isOpen={Boolean(selectedAction)}
          handleClose={() => {
            setSelectedAction(null);
          }}
          action={selectedAction!}
          title={getTitle(selectedAction!)}
          subtitle={
            application
              ? `${application?.applicant?.name} - ${application.position}`
              : selectedApplicationsIds
                ? `${selectedApplicationsIds.length} ${
                    selectedApplicationsIds.length === 1
                      ? 'application'
                      : 'applications'
                  } selected`
                : ''
          }
          isSubtitleShown
          submitWithMessageText={getSendWithMessageButtonText(selectedAction!)}
          submitWithoutMessageText={getSendWithoutMessageButtonText(
            selectedAction!,
          )}
          showSendWithoutMessage={selectedAction?.showSendWithoutMessage}
          errorText={errorText}
          setErrorText={setErrorText}
          buttonColor={getCTAButtonColor(selectedAction?.title)}
          handleSubmit={isCompany ? handleSend : handleSendEnterprise}
          showProgress={
            isUpdateInterviewActionLoading ||
            isCreateApplicationActionLoading ||
            isEnterpriseCreateBulkActionLoading ||
            isCreateBulkActionLoading
          }
          disabled={
            isCreateApplicationActionLoading ||
            isEnterpriseCreateBulkActionLoading ||
            isCreateBulkActionLoading
          }
        />
      )}
    </>
  );
};

export default MarkInterviewModal;
